import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { environment } from "../../../environments/environment";
import { CacheAreaKey } from "../config/cache-config";
import { ConnectStore } from "../stores/auth.store";
import { AuthService } from "./auth";
import { StorageHelper } from "./storage";

export enum HttpResponseCode {
  NotFound = 404,
  OK = 200,
  InternalServerError = 500,
  Unauthorized = 401
}

export interface RequestConfig extends AxiosRequestConfig {
  retry?: number;
  retryDelay?: number;
  headers?: Headers;
  [key: string]: unknown;
}

const globalConfig: RequestConfig = {
  retry: 0,
  retryDelay: 1000,
};

abstract class HttpBase {

  protected readonly instance: AxiosInstance;

  public constructor(baseURL?: string) {
    this.instance = axios.create({
      baseURL,
    });
    this.instance.interceptors.request.use(
      (config) => {
        const connectStore = StorageHelper.GetInstance.getFromCache('conf', 'connect', CacheAreaKey.Customer) as ConnectStore;
        if (connectStore) {
          // config.headers['Access-Control-Allow-Origin'] = `*`;
          config.headers['Authorization'] = `Bearer ${connectStore.id_token}`;
          config.headers['x-authorization'] = `${connectStore.access_token}`;
          config.headers['provider'] = `connect`;
          config.headers['x-businessunit'] = `dstv`;
          config.headers['x-country-iso'] = `south africa`;
          config.headers['x-profile'] = `Residential`;
        }
        return config;
      }
    );
    this.instance.interceptors.response.use(
      (config) => {
        return config;
      },
      (error) => {
        const { config, response } = error;
        if (response?.status === 401 && `${config?.url}`.startsWith(`${environment.UTILITY_API_URI}/uni-api/dvs`)) {
          AuthService.logout();
          AuthService.signIn(`/confirm-package/${StorageHelper.GetInstance.getFromCache('conf', 'package', CacheAreaKey.Config)}`)
        }

        if (!config || !config.retry) {
          return Promise.reject(error);
        }
        config.retry -= 1
        const delayRetryRequest = new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, config.retryDelay || 0)
        })
        return delayRetryRequest.then(() => this.instance(config));
      }
    );
  }

}

export class HttpClient extends HttpBase {
  private requests: Map<string, Promise<AxiosResponse<unknown>>> = new Map();
  private static httpInstance: HttpClient;

  private constructor() {
    super();
  }

  public static get GetInstance() {
    if (!this.httpInstance) {
      this.httpInstance = new HttpClient();
    }

    return this.httpInstance;
  }

  private static RequestKey(...args: unknown[]): string {
    try {
      return JSON.stringify(args)
    } catch (err) {
      console.error('Invalid RequestKey: ', args);
      throw new Error('Invalid RequestKey')
    }
  }

  public get<T>(url: string, options: RequestConfig = {}, allowDuplicates = false): Promise<AxiosResponse<T>> {
    const key = HttpClient.RequestKey(url, options);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const request = this.instance.get<T>(url, { ...globalConfig, ...options })
      .then(res => {
        this.requests.delete(key);
        return res;
      })
      .catch(error => {
        console.error('Failed get request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  public getBlob<T>(url: string, options: RequestConfig = {}, allowDuplicates = false): Promise<AxiosResponse<T>> {
    const key = HttpClient.RequestKey(url, options);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const request = this.instance.get<T>(url, { ...options, responseType: 'blob' })
      .then(res => {
        this.requests.delete(key);
        return res;
      })
      .catch(error => {
        console.error('Failed get request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  public post<K, T>(url: string, data?: K, options: RequestConfig = {}, allowDuplicates = false): Promise<AxiosResponse<T>> {
    const key = HttpClient.RequestKey(url, data, options);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const request = this.instance.post<T>(url, data, { ...globalConfig, ...options })
      .then(res => {
        this.requests.delete(key);
        return res;
      })
      .catch(error => {
        console.error('Failed post request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  public put<K, T>(url: string, data: K, options: RequestConfig = {}, allowDuplicates = false): Promise<AxiosResponse<T>> {
    const key = HttpClient.RequestKey(url, data, options);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const request = this.instance.put<T>(url, data, options)
      .then(res => {
        this.requests.delete(key);
        return res;
      })
      .catch(error => {
        console.error('Failed put request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  public delete<K, T>(url: string, data: K, options: RequestConfig = {}, allowDuplicates = false): Promise<AxiosResponse<T>> {
    const requestOptions = {
      ...options,
      body: data
    };
    const key = HttpClient.RequestKey(url, requestOptions);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const request = this.instance.delete<T>(url, requestOptions)
      .then(res => {
        this.requests.delete(key);
        return res;
      })
      .catch(error => {
        console.error('Failed delete request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }
}